import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Page } from '@/components/layout/Page';
import { linkify } from '@/pages/Overview/entities';

/* ── Colors ───────────────────────────────────────────────────────────── */
const C = {
  blueDark: '#274483',
  t1: '#1A1C2E',
  t2: '#5C5F72',
  t3: '#9295A5',
  border: '#E4E0D8',
  warm: '#F5F6F8',
  white: '#FFFFFF',
  pos: '#2A8055',
  neg: '#BC2E3D',
  warn: '#D27A24',
};
const tone = (c?: string) =>
  c === 'positive'
    ? C.pos
    : c === 'warning'
      ? C.warn
      : c === 'negative'
        ? C.neg
        : c === 'muted'
          ? C.t3
          : c === 'text2'
            ? C.t2
            : c === 'blueDark'
              ? C.blueDark
              : C.t1;
const toneBg = (c?: string) =>
  c === 'positive'
    ? 'rgba(42,128,85,.1)'
    : c === 'warning'
      ? 'rgba(210,122,36,.1)'
      : c === 'negative'
        ? 'rgba(188,46,61,.1)'
        : 'rgba(39,68,131,.08)';

/* ── Types ────────────────────────────────────────────────────────────── */
type Kpi = {
  label: string;
  value: string;
  sub: string;
  pct: number;
  targetPct?: number;
  barColor: string;
  badge: string;
  badgeColor: string;
};
type Queue = {
  name: string;
  status: string;
  statusColor: string;
  metaPre: string;
  lock: string;
  lockColor: string;
  desc: string;
  link: string | null;
};
type Need = {
  icon: string;
  iconTone: string;
  title: string;
  desc: string;
  badge: string;
  badgeColor: string;
  cta: string;
  ctaLink: string | null;
};
type CustomerHealth = {
  id: string;
  name: string;
  delta: string;
  status: string;
  statusColor: string;
  memory: string;
  note: string;
};
type OverviewData = {
  status: string;
  kpisLabel: string;
  kpis: Kpi[];
  queuesLabel: string;
  queues: Queue[];
  needsCount: string;
  needs: Need[];
  customerHealth: CustomerHealth[];
};

const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: 8 };
const eyebrow: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: C.t3,
  textTransform: 'uppercase',
  letterSpacing: '.09em',
};
const pill = (c: string): React.CSSProperties => ({
  fontSize: 9.5,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 100,
  background: toneBg(c),
  color: tone(c),
});

/* A section wrapper: heading sits at the top of the section (matches "What Needs You Today"). */
function SectionCard({
  title,
  right,
  bodyStyle,
  children,
}: {
  title: string;
  right?: React.ReactNode;
  bodyStyle?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <section style={{ ...card, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          padding: '11px 14px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{title}</div>
        {right}
      </div>
      <div style={{ flex: 1, ...bodyStyle }}>{children}</div>
    </section>
  );
}

export function Home() {
  const { data } = useQuery<OverviewData>({
    queryKey: ['overview', 'home'],
    queryFn: () => fetch('/api/overview/home').then((r) => r.json()),
  });

  const tagline = useMemo(() => {
    const now = new Date();
    const dateStr = now.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
    const hr = now.getHours();
    const greet = hr < 12 ? 'Good morning' : hr < 18 ? 'Good afternoon' : 'Good evening';
    return `${greet}, Carlene · ${dateStr}`;
  }, []);

  return (
    <Page
      title="Overview"
      subheader={tagline}
      actions={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            alignSelf: 'flex-start',
            marginTop: 6,
          }}
        >
          <div className="animate-pulse" style={{ width: 7, height: 7, borderRadius: '50%', background: C.pos }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: C.pos }}>{data?.status ?? 'All systems live'}</span>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4 items-start">
        {/* ── Top-left: KPI tracking to Q2 plan ── */}
        <SectionCard
          title={data?.kpisLabel ?? 'KPI tracking to Q2 plan'}
          bodyStyle={{ background: C.warm, padding: 14 }}
        >
          <div className="grid grid-cols-2 gap-3">
            {(data?.kpis ?? []).map((k) => (
              <div key={k.label} style={{ ...card, padding: '14px 16px' }}>
                <div style={{ ...eyebrow, marginBottom: 8 }}>{k.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: C.t1, letterSpacing: '-0.03em' }}>{k.value}</div>
                  <div style={{ fontSize: 10, color: C.t3 }}>{k.sub}</div>
                </div>
                <div
                  style={{ height: 4, borderRadius: 2, background: C.border, marginBottom: 6, position: 'relative' }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${k.pct}%`,
                      borderRadius: 2,
                      background: tone(k.barColor),
                    }}
                  />
                  {k.targetPct != null && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${Math.min(k.targetPct, 100)}%`,
                        top: -3,
                        height: 10,
                        width: 2,
                        background: C.t3,
                        borderRadius: 1,
                        transform: k.targetPct >= 100 ? 'translateX(-2px)' : undefined,
                      }}
                    />
                  )}
                </div>
                <span style={pill(k.badgeColor)}>{k.badge}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Top-right: Active co-planning queues for Q3 ── */}
        <SectionCard
          title={data?.queuesLabel ?? 'Active co-planning queues for Q3'}
          bodyStyle={{ background: C.warm, padding: 14 }}
        >
          <div className="grid grid-cols-2 gap-3">
            {(data?.queues ?? []).map((q) => {
              const inner = (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: C.t1 }}>{q.name}</div>
                    <span style={{ ...pill(q.statusColor), fontSize: 9, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {q.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: C.t3 }}>
                    {q.metaPre}
                    <span style={{ fontWeight: 700, color: tone(q.lockColor) }}>{q.lock}</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.5 }}>{linkify(q.desc)}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: q.link ? C.blueDark : C.t3, marginTop: 'auto' }}>
                    Open app →
                  </div>
                </>
              );
              const base: React.CSSProperties = {
                ...card,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                textDecoration: 'none',
              };
              return q.link ? (
                <Link key={q.name} to={q.link} style={base}>
                  {inner}
                </Link>
              ) : (
                <div key={q.name} style={base}>
                  {inner}
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* ── Bottom-left: What Needs You Today ── */}
        <SectionCard
          title="What Needs You Today"
          right={<span style={pill('negative')}>{data?.needsCount ?? ''}</span>}
        >
          {(data?.needs ?? []).map((n, i, arr) => (
            <div
              key={n.title}
              style={{
                padding: '12px 14px',
                borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
                display: 'flex',
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  background: toneBg(n.iconTone),
                  border: `1px solid ${tone(n.iconTone)}33`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  flexShrink: 0,
                  marginTop: 1,
                  color: tone(n.iconTone),
                }}
              >
                {n.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, marginBottom: 2 }}>{linkify(n.title)}</div>
                <div style={{ fontSize: 10.5, color: C.t2, lineHeight: 1.5, marginBottom: 6 }}>{linkify(n.desc)}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ ...pill(n.badgeColor), fontSize: 9, padding: '2px 7px' }}>{n.badge}</span>
                  {n.ctaLink ? (
                    <Link
                      to={n.ctaLink}
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.blueDark,
                        marginLeft: 'auto',
                        textDecoration: 'none',
                      }}
                    >
                      {n.cta}
                    </Link>
                  ) : (
                    <span style={{ fontSize: 9, fontWeight: 700, color: C.blueDark, marginLeft: 'auto' }}>{n.cta}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </SectionCard>

        {/* ── Bottom-right: Customer Demand Health ── */}
        <SectionCard
          title="Customer Demand Health"
          right={
            <Link
              to="/data/customers"
              style={{ fontSize: 10, fontWeight: 700, color: C.blueDark, textDecoration: 'none' }}
            >
              All customers →
            </Link>
          }
        >
          {(data?.customerHealth ?? []).map((c, i, arr) => (
            <div
              key={c.id}
              style={{ padding: '12px 16px', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                <Link
                  to={`/data/customers?id=${encodeURIComponent(c.id)}`}
                  style={{ fontSize: 11, fontWeight: 700, color: C.blueDark, textDecoration: 'none' }}
                >
                  {c.name}
                </Link>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>{c.delta}</span>
                  <span style={{ ...pill(c.statusColor), fontSize: 9, padding: '1px 6px' }}>{c.status}</span>
                </div>
              </div>
              <div style={{ fontSize: 9.5, color: C.t3, marginBottom: 2 }}>{c.memory}</div>
              <div style={{ fontSize: 9.5, color: C.t3 }}>{c.note}</div>
            </div>
          ))}
        </SectionCard>
      </div>
    </Page>
  );
}
